/* Hash routing, in thirty lines, because this app has five screens.

   The hash rather than the History API: the SPA is served by FastAPI, and a
   hash never reaches the server, so a deep link cannot 404 against a route
   Python has never heard of. Deep links matter here -- "look at Harness
   Engineering" is a link somebody pastes into Slack. */
import { useEffect, useState } from "react";

export type Route =
  | { view: "portfolio"; kind: "service" | "asset" }
  | { view: "new"; kind: "service" | "asset" }
  | { view: "strategy" }
  | { view: "bet"; id: number }
  | { view: "initiative"; id: number }
  | { view: "board" }
  | { view: "tasks" }
  | { view: "graph" }
  | { view: "settings" }
  | { view: "object"; kind: "service" | "asset"; id: number }
  | { view: "task"; id: number };

export function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] === "service" && parts[1]) return { view: "object", kind: "service", id: +parts[1] };
  if (parts[0] === "asset" && parts[1]) return { view: "object", kind: "asset", id: +parts[1] };
  if (parts[0] === "task" && parts[1]) return { view: "task", id: +parts[1] };
  if (parts[0] === "bet" && parts[1]) return { view: "bet", id: +parts[1] };
  if (parts[0] === "initiative" && parts[1]) return { view: "initiative", id: +parts[1] };
  if (parts[0] === "strategy") return { view: "strategy" };
  /* `#/services/new` rather than `#/service/new`: the singular routes above
     take an id, and `+"new"` is NaN, which would render a detail page for
     object NaN. Hanging it off the plural list route keeps the two apart. */
  if (parts[0] === "services" && parts[1] === "new") return { view: "new", kind: "service" };
  if (parts[0] === "assets" && parts[1] === "new") return { view: "new", kind: "asset" };
  if (parts[0] === "assets") return { view: "portfolio", kind: "asset" };
  if (parts[0] === "services") return { view: "portfolio", kind: "service" };
  if (parts[0] === "board") return { view: "board" };
  if (parts[0] === "tasks") return { view: "tasks" };
  if (parts[0] === "graph") return { view: "graph" };
  if (parts[0] === "settings") return { view: "settings" };
  return { view: "portfolio", kind: "service" };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export const go = (path: string) => {
  window.location.hash = path;
};

export const href = {
  object: (kind: "service" | "asset", id: number) => `#/${kind}/${id}`,
  task: (id: number) => `#/task/${id}`,
  /* The list a given kind belongs to. Singular routes are details, plural are
     lists: #/service/6 is Harness Engineering, #/services is the table it sits
     in. */
  list: (kind: "service" | "asset") => (kind === "service" ? "#/" : "#/assets"),
  create: (kind: "service" | "asset") => (kind === "service" ? "#/services/new" : "#/assets/new"),
  strategy: "#/strategy",
  bet: (id: number) => `#/bet/${id}`,
  initiative: (id: number) => `#/initiative/${id}`,
};
